import { Fragment, useEffect, useMemo, useState } from 'react'
import { api, type Journalzeile, type Rechnung, type Rechnungsbild } from '../api'
import { anzahl, euro, menge, zahlwert } from '../format'
import { Karte } from '../komponenten/Karte'
import { Kennzahl } from '../komponenten/Kennzahl'

/* Die Rechnungen eines Tages, jede aufklappbar bis auf die einzelne
 * Buchung.
 *
 * Das ist die Ansicht, auf der das Audit steht. Alles andere — Quoten,
 * Prüfregeln, Auffälligkeiten — rechnet auf dem, was hier steht. Sitzt
 * hier ein Anhänger falsch, wertet jede Regel darüber falsch aus, und
 * zwar unauffällig: Eine Umbuchung, die als Storno gilt, sieht in jeder
 * Kennzahl aus wie ein Storno.
 *
 * ## Warum die Anhänger aus dem Bericht kommen und nicht aus dem Archiv
 *
 * Das Archiv kennt die negative Zeile, aber nicht ihren Grund. Am
 * 12.09. stehen dort 1.316 negative Umsatzzeilen — Stornos sind davon
 * 75. Die anderen 1.241 sind die Kasse beim Teilen und Umbuchen. Der
 * Transaktionsbericht trägt die Art in einer eigenen Spalte, und
 * `Referenz` verbindet die beiden Hälften einer Umbuchung.
 *
 * ## Warum Teilrechnung und Fremdbuchung verschieden aussehen
 *
 * Beide bekommen Positionen von einem anderen Tisch. Die Teilrechnung
 * gehört zu derselben Tischbelegung — sie wird eingerückt. Die
 * Fremdbuchung geht auf einen **anderen** Tisch, zu anderen Gästen, mit
 * eigener Rechnung; sie steht für sich, und woher ihre Positionen
 * kommen, steht als Hinweis daran.
 *
 * ## Die Bedienernummer, nicht der Name
 *
 * Angezeigt wird die Kennung, die die Kasse vergibt. Namen gibt die API
 * nicht heraus — sie sind beim Einlesen gehasht worden.
 */

/* Der Ton eines Anhaengers — die Farbe, in der er steht.
 *
 * Sieben Toene fuer sieben Bedeutungen, nicht sieben Farben fuer sieben
 * Arten: `Umbuchung` und `Teilrechnung` sind dasselbe Geschehen aus zwei
 * Richtungen und tragen deshalb denselben Ton. Wer die Farben nach Arten
 * vergibt, bekommt einen Farbkasten und keine Aussage.
 *
 * `buchung` ist mit Absicht der leiseste: Von 131 Zeilen eines Tisches
 * sind 76 Buchungen.
 */
type Ton = 'buchung' | 'bewegung' | 'fremd' | 'storno' | 'geld'
         | 'nachlass' | 'still'

type Anhaenger = { name: string; ton: Ton }

/** Wie die Kasse die Art nennt und wie sie hier heißt.
 *
 * An **einer** Stelle. Zwei Wortlaute für dieselbe Sache sind in diesem
 * Projekt schon einmal 38 Stellen geworden.
 */
const BUCHUNGSART: Record<string, Anhaenger> = {
  SALE: { name: 'Buchung', ton: 'buchung' },
  TRANSFER: { name: 'Umbuchung', ton: 'bewegung' },
  SPLIT: { name: 'Teilrechnung', ton: 'bewegung' },
  FOREIGN: { name: 'Fremdbuchung', ton: 'fremd' },
  UPDATE: { name: 'Änderung', ton: 'still' },
  VOID: { name: 'Storno', ton: 'storno' },
  RECALL: { name: 'Wiederaufruf', ton: 'fremd' },
}

const ZAHLART: Record<string, Anhaenger> = {
  PAYMENT: { name: 'Bezahlung', ton: 'geld' },
  VOID: { name: 'Zahlung storniert', ton: 'storno' },
  TRANSFER: { name: 'Zahlung umgebucht', ton: 'bewegung' },
}

/* In welche Richtung die Positionen laufen.
 *
 * `TRANSFER` und `VOID` nehmen sie von diesem Konto herunter, `SPLIT`,
 * `FOREIGN` und `RECALL` setzen sie darauf. Der Pfeil sagt das, und er
 * sagt es falsch herum, wenn man ihn an der Art vorbei rät: Ein Storno
 * mit Gegenkonto heißt „ging dorthin", nicht „kam von dort".
 *
 * Bei `UPDATE` ist die Richtung nicht bestimmt — dann steht kein Pfeil.
 */
const RICHTUNG: Record<string, string> = {
  TRANSFER: '→', VOID: '→',
  SPLIT: '←', FOREIGN: '←', RECALL: '←',
}

const ZAHLART_KIND: Record<string, string> = {
  CASH: 'Bar', CARD: 'Karte', VOUCHER: 'Gutschein', NONE: 'ohne Zahlung',
}

function anhaenger(z: Journalzeile): Anhaenger[] {
  const liste: Anhaenger[] = []
  // **Der Sofortstorno ersetzt „Buchung", er tritt nicht daneben.**
  // Eine Buchung, die nichts gebucht hat, ist keine Buchung — und zwei
  // Anhänger an derselben Zeile lesen sich wie zwei Vorgänge.
  const sofort = !z.ist_zahlung && z.art === 'SALE'
    && zahlwert(z.quantity) === 0
  const haupt = z.ist_zahlung ? ZAHLART[z.art] : BUCHUNGSART[z.art]
  // Eine unbekannte Art bekommt ihren rohen Namen und den stillen Ton —
  // nicht gar keinen Anhaenger. Eine Kasse darf ihre Vorgaenge nennen,
  // wie sie will; dann steht hier eben, wie sie sie nennt.
  if (!sofort) liste.push(haupt ?? { name: z.art, ton: 'still' })

  if (z.ist_zahlung) {
    // Die Art nur, wenn sie nicht schon im Namen der Zahlart steht.
    // Sonst stand an der Barzahlung „Bar · Bezahlung · Bar".
    const k = z.zahlart_kind ? ZAHLART_KIND[z.zahlart_kind] : null
    if (k && k.toLowerCase() !== (z.method_name ?? '').toLowerCase()) {
      liste.push({ name: k, ton: 'still' })
    }
  } else {
    if (zahlwert(z.discount) !== 0) liste.push({ name: 'Rabatt', ton: 'nachlass' })
    if (zahlwert(z.free) !== 0) liste.push({ name: 'Aufs Haus', ton: 'nachlass' })
    if (zahlwert(z.breakage) !== 0) liste.push({ name: 'Bruch', ton: 'nachlass' })
    // **Der Sofortstorno: eine Buchung, die nichts gebucht hat.**
    //
    // Dass er stattfand, sagt das Journal — `SALE` mit Menge 0. Was er
    // wert war, sagt nur das Archiv; der Transaktionsbericht führt
    // dieselben Zeilen mit Einzelpreis 0,00. Deshalb steht der Anhänger
    // auch ohne Archiv da, und der Betrag nur mit.
    //
    // Der Preis fehlt außerdem bei Zeilen, die nie einen hatten:
    // Kopfartikel einer Zusammenstellung („Arg. Filet"), Abwahlen
    // („ohne House Fries"), Küchenanweisungen („NACH …"). Sie gehören
    // zum selben Vorgang und werden deshalb mitgekennzeichnet — 66 von
    // 220 am 12.09.
    if (sofort) {
      liste.push({
        name: z.sofort_preis !== null && z.sofort_preis !== undefined
          ? `Sofortstorno ${euro(z.sofort_preis)}`
          : 'Sofortstorno',
        ton: 'storno',
      })
    }
  }
  return liste
}

function Marken({ liste }: { liste: Anhaenger[] }) {
  return (
    <>
      {liste.map((a) => (
        <span key={a.name} className={`marke ton-${a.ton}`}>{a.name}</span>
      ))}
    </>
  )
}

/* Eine Aufteilung ist **eine** Handlung, auch wenn sie vierzehn Zeilen
 * schreibt. Wer einen Tisch teilt, sieht im Journal sonst vierzig
 * Zeilen „Umbuchung" hintereinander und findet die Buchung nicht mehr,
 * die dazwischen steht. Zusammengefasst wird nur, was zusammengehört:
 * dieselbe Art, dasselbe Gegenkonto, dieselbe Minute. */
type Bündel = { schluessel: string; zeilen: Journalzeile[] }

function buendeln(zeilen: Journalzeile[]): Bündel[] {
  const aus: Bündel[] = []
  for (const z of zeilen) {
    const verschiebt = !z.ist_zahlung
      && (z.art === 'TRANSFER' || z.art === 'SPLIT' || z.art === 'FOREIGN')
    const schluessel = verschiebt
      ? `${z.art}|${z.gegenkonto_ref ?? '?'}|${z.zeit}`
      : `einzeln|${z.ist_zahlung}|${z.row_no}`
    const letztes = aus[aus.length - 1]
    if (verschiebt && letztes && letztes.schluessel === schluessel) {
      letztes.zeilen.push(z)
    } else {
      aus.push({ schluessel, zeilen: [z] })
    }
  }
  return aus
}

function Journalzeilen({ zeilen }: { zeilen: Journalzeile[] }) {
  const [offen, setOffen] = useState<string[]>([])
  const buendel = useMemo(() => buendeln(zeilen), [zeilen])

  return (
    <table className="journal">
      <thead>
        <tr>
          <th style={{ width: '3.5em' }}>Zeit</th>
          <th>Vorgang</th>
          <th className="r" style={{ width: '4em' }}>Menge</th>
          <th className="r" style={{ width: '7em' }}>Betrag</th>
          <th className="r" style={{ width: '6em' }}>Bediener</th>
        </tr>
      </thead>
      <tbody>
        {buendel.map((b) => {
          const z = b.zeilen[0]
          const viele = b.zeilen.length > 1
          const auf = offen.includes(b.schluessel)
          const summe = b.zeilen.reduce((s, x) => s + zahlwert(x.line_gross), 0)

          return (
            <Fragment key={b.schluessel}>
              <tr>
                <td className="dezent zahl">{z.zeit}</td>
                {/* Drei Zeilen, nicht eine: der Artikel, was fuer ein
                    Vorgang das war, und die Warengruppe. Nebeneinander
                    lief das ineinander. */}
                <td>
                  <div>
                    {viele ? (
                      <button className="verweis" aria-expanded={auf}
                              onClick={() => setOffen((v) => auf
                                ? v.filter((x) => x !== b.schluessel)
                                : [...v, b.schluessel])}>
                        {auf ? '▾' : '▸'} {anzahl(b.zeilen.length)} Positionen
                      </button>
                    ) : (
                      <span>{z.article_text ?? z.method_name ?? '—'}</span>
                    )}
                  </div>

                  <div className="vorgang-marken">
                    <Marken liste={anhaenger(z)} />
                    {z.ist_zahlung && zahlwert(z.tip) !== 0 && (
                      <span className="marke ton-geld">
                        Trinkgeld {euro(z.tip)}
                      </span>
                    )}
                    {/* Das Gegenkonto steht bei seinem Anhaenger, nicht
                        eine Zeile tiefer: „Umbuchung" und „→ Tisch 671.5"
                        sind eine Aussage. */}
                    {z.gegenkonto && RICHTUNG[z.art] && (
                      <span className="dezent">
                        {RICHTUNG[z.art]} {z.gegenkonto}
                      </span>
                    )}
                  </div>

                  {!viele && z.group_name && (
                    <div className="vorgang-gruppe">{z.group_name}</div>
                  )}
                </td>
                <td className="r zahl dezent">
                  {viele ? '' : z.quantity === null ? '' : menge(z.quantity)}
                </td>
                <td className="r zahl">{euro(summe)}</td>
                <td className="r zahl dezent">{z.operator_ref ?? '—'}</td>
              </tr>

              {viele && auf && b.zeilen.map((x) => (
                <tr key={`${b.schluessel}-${x.row_no}`} className="journal-teil">
                  <td />
                  <td style={{ paddingLeft: 26 }}>
                    <div>{x.article_text ?? '—'}</div>
                    {x.group_name && (
                      <div className="vorgang-gruppe">{x.group_name}</div>
                    )}
                  </td>
                  <td className="r zahl dezent">{menge(x.quantity)}</td>
                  <td className="r zahl">{euro(x.line_gross)}</td>
                  <td />
                </tr>
              ))}
            </Fragment>
          )
        })}
      </tbody>
    </table>
  )
}

function Journal({ konto, tag, betriebe }: {
  konto: string; tag: string; betriebe: string[]
}) {
  const [zeilen, setZeilen] = useState<Journalzeile[] | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    let weg = false
    setZeilen(null); setFehler(null)
    api.rechnung(konto, tag, betriebe)
      .then((x) => { if (!weg) setZeilen(x.journal) })
      .catch((e) => { if (!weg) setFehler(e.message) })
    return () => { weg = true }
  }, [konto, tag, betriebe.join(',')])

  if (fehler) return <div className="fehler">{fehler}</div>
  if (!zeilen) return <div className="laedt">Wird geladen …</div>
  if (!zeilen.length) {
    return <div className="leer">Zu dieser Rechnung steht keine Buchung im Journal.</div>
  }
  return <Journalzeilen zeilen={zeilen} />
}

function Zeile({ r, tag, betriebe, offen, umschalten }: {
  r: Rechnung; tag: string; betriebe: string[]
  offen: boolean; umschalten: () => void
}) {
  const teil = r.haupt_art === 'SPLIT'
  const name = r.account_name ?? r.allocation_key

  return (
    <>
      <tr className={teil ? 'rechnung-teil' : undefined}>
        <td style={teil ? { paddingLeft: 30 } : undefined}>
          <div>
            <button className="verweis" aria-expanded={offen} onClick={umschalten}>
              {offen ? '▾' : '▸'} {name}
            </button>
            {teil && <span className="marke ton-bewegung">Teilrechnung</span>}
            {r.haupt_art === 'FOREIGN' && (
              <span className="marke ton-fremd">Fremdbuchung</span>
            )}
          </div>
          {/* Die Herkunft auf eigener Zeile: Neben dem Anhaenger brach
              „← 04 Terrasse 600, Tisch 671" mitten im Namen um. */}
          {r.haupt_name && !teil && (
            <div className="vorgang-gruppe">← {r.haupt_name}</div>
          )}
        </td>
        <td className="dezent zahl">{r.von}–{r.bis}</td>
        <td className="r zahl">{anzahl(r.buchungen)}</td>
        <td className="r zahl">
          {r.raus || r.rein
            ? <span>{r.raus ? `${anzahl(r.raus)} raus` : ''}
                {r.raus && r.rein ? ' · ' : ''}
                {r.rein ? `${anzahl(r.rein)} rein` : ''}</span>
            : <span className="dezent">–</span>}
        </td>
        {/* Storno und Sofortstorno stehen in einer Spalte und sind
            zwei Dinge (S-14): Der eine nimmt gebuchtes Geld zurueck,
            der andere hat nie welches gebucht. Deshalb untereinander
            und beide mit ihrem Wort. */}
        <td className="r zahl">
          {r.stornos > 0 && (
            <div className="storniert">
              {anzahl(r.stornos)} · {euro(r.storno_summe)}
            </div>
          )}
          {r.sofort_anzahl > 0 && (
            <div className="dezent" style={{ fontSize: 11 }}>
              {anzahl(r.sofort_anzahl)} sofort
              {r.sofort_summe !== null && ` · ${euro(r.sofort_summe)}`}
            </div>
          )}
          {!r.stornos && !r.sofort_anzahl && <span className="dezent">–</span>}
        </td>
        <td className="r zahl">
          {r.bezahlt === null ? <span className="dezent">offen</span> : euro(r.bezahlt)}
        </td>
        <td className="r zahl dezent">
          {zahlwert(r.trinkgeld) ? euro(r.trinkgeld) : '–'}
        </td>
        <td className="dezent">{r.zahlarten ?? '–'}</td>
        <td className="r zahl dezent">{(r.bediener ?? []).join(', ') || '–'}</td>
      </tr>

      {offen && (
        <tr className="rechnung-journal">
          <td colSpan={9}>
            <Journal konto={r.allocation_key} tag={tag} betriebe={betriebe} />
          </td>
        </tr>
      )}
    </>
  )
}

export function Rechnungen({ datum, betriebe }: {
  datum: string; betriebe: string[]
}) {
  const [d, setD] = useState<Rechnungsbild | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laedt, setLaedt] = useState(true)
  const [offen, setOffen] = useState<string[]>([])
  const [suche, setSuche] = useState('')

  useEffect(() => {
    let weg = false
    setLaedt(true); setFehler(null); setOffen([])
    api.rechnungen(datum, betriebe)
      .then((x) => { if (!weg) setD(x) })
      .catch((e) => { if (!weg) { setFehler(e.message); setD(null) } })
      .finally(() => { if (!weg) setLaedt(false) })
    return () => { weg = true }
  }, [datum, betriebe.join(',')])

  const gefiltert = useMemo(() => {
    if (!d) return []
    const s = suche.trim().toLowerCase()
    if (!s) return d.rechnungen
    // Gesucht wird über Tisch, Bereich, Kontonummer und Bediener. Eine
    // gefundene Teilrechnung bringt ihren Haupttisch mit — sonst stünde
    // sie ohne den Vorgang da, aus dem sie entstanden ist.
    const treffer = new Set<string>()
    for (const r of d.rechnungen) {
      const text = [r.account_name, r.area, r.table_no, r.allocation_key,
                    ...(r.bediener ?? [])].join(' ').toLowerCase()
      if (text.includes(s)) {
        treffer.add(r.allocation_key)
        if (r.haupt) treffer.add(r.haupt)
      }
    }
    return d.rechnungen.filter((r) => treffer.has(r.allocation_key))
  }, [d, suche])

  if (fehler) return <div className="fehler">{fehler}</div>
  if (laedt && !d) return <div className="laedt">Wird geladen …</div>
  if (!d) return null

  if (!d.journal_vorhanden) {
    return (
      <Karte titel="Rechnungen">
        <div className="leer">
          Für diesen Tag liegt kein Transaktionsbericht vor. Das
          Buchungsjournal steht nur dort — das Archiv kennt die negative
          Zeile, aber nicht ihren Grund. Ohne den Bericht lässt sich
          nicht sagen, ob eine Rücknahme ein Storno war oder eine
          Umbuchung.
        </div>
      </Karte>
    )
  }

  const haupt = d.rechnungen.filter((r) => r.haupt_art !== 'SPLIT')
  const teile = d.rechnungen.length - haupt.length
  const umbuchungen = d.rechnungen.reduce((s, r) => s + r.raus, 0)
  const fremd = d.rechnungen.reduce((s, r) => s + r.fremd, 0)
  const stornos = d.rechnungen.reduce((s, r) => s + r.stornos, 0)
  const stornosumme = d.rechnungen.reduce(
    (s, r) => s + Math.abs(zahlwert(r.storno_summe)), 0)
  const sofort = d.rechnungen.reduce((s, r) => s + r.sofort_anzahl, 0)
  const sofortsumme = d.rechnungen.reduce((s, r) => s + zahlwert(r.sofort_summe), 0)
  const sofortPreislos = sofort - d.rechnungen.reduce(
    (s, r) => s + r.sofort_mit_preis, 0)

  return (
    <>
      <div className="gitter g-3">
        <Kennzahl etikett="Rechnungen" wert={anzahl(d.rechnungen.length)}
                  fuss={`${anzahl(haupt.length)} Tischbelegungen, ${anzahl(teile)} Teilrechnungen`} />
        <Kennzahl etikett="Umbuchungen" wert={anzahl(umbuchungen)}
                  fuss={`davon ${anzahl(fremd)} auf einen anderen Tisch`} />
        <Kennzahl etikett="Stornos" wert={anzahl(stornos)}
                  fuss={`${euro(stornosumme)} — aus dem Bericht, nicht aus dem Vorzeichen`} />
      </div>

      {/* Der Sofortstorno steht für sich, und die Kachel sagt warum:
          Er ist **kein Umsatz** (gebucht wurden 0,00 €), und beziffert
          wird er allein vom Einzelpreis. Ohne Archiv steht die Zahl da
          und der Betrag nicht — auch das gehört gesagt. */}
      <div className="gitter g-2-1" style={{ marginTop: 14 }}>
        <Kennzahl
          etikett="Sofortstornos"
          wert={anzahl(sofort)}
          fuss={d.archiv_vorhanden
            ? `${euro(sofortsumme)} Einzelpreise — kein Umsatz, gebucht wurden 0,00 €`
            : 'Der Betrag steht nur im Archiv, und das liegt für diesen Tag nicht vor'} />
        <div className="karte">
          <div className="karte-leib">
            <p className="merker" style={{ margin: 0 }}>
              Eine Buchung, die nichts gebucht hat: Menge null, Betrag
              null. <b>Jede betragsbasierte Suche verliert sie.</b> Dass
              sie stattfand, sagt der Transaktionsbericht; was sie wert
              war, steht nur im Archiv.
              {sofortPreislos > 0 && ` ${anzahl(sofortPreislos)} der Zeilen
                tragen keinen Preis — Kopfartikel, Abwahlen und
                Küchenanweisungen haben nie einen.`}
            </p>
          </div>
        </div>
      </div>

      <Karte titel="Rechnungen"
             neben="aufklappen zeigt das Buchungsjournal"
             werkzeug={
               <input type="search" value={suche} placeholder="Tisch, Konto, Bediener …"
                      onChange={(e) => setSuche(e.target.value)}
                      aria-label="Rechnungen durchsuchen"
                      style={{ padding: '6px 8px', fontSize: 13, minWidth: 210 }} />
             }>
        <table className="rechnungen">
          <thead>
            <tr>
              <th>Tisch</th>
              <th>Zeit</th>
              <th className="r">Buchungen</th>
              <th className="r">Umbuchungen</th>
              <th className="r">Stornos</th>
              <th className="r">bezahlt</th>
              <th className="r">Trinkgeld</th>
              <th>Zahlart</th>
              <th className="r">Bediener</th>
            </tr>
          </thead>
          <tbody>
            {gefiltert.map((r) => (
              <Zeile key={r.allocation_key} r={r} tag={d.tag} betriebe={betriebe}
                     offen={offen.includes(r.allocation_key)}
                     umschalten={() => setOffen((v) =>
                       v.includes(r.allocation_key)
                         ? v.filter((x) => x !== r.allocation_key)
                         : [...v, r.allocation_key])} />
            ))}
          </tbody>
        </table>

        {!gefiltert.length && (
          <div className="leer">Keine Rechnung passt zu „{suche}“.</div>
        )}

        <p className="merker" style={{ marginTop: 14 }}>
          Die Anhänger kommen aus dem Transaktionsbericht, nicht aus dem
          Vorzeichen im Archiv. Angezeigt wird die Bedienernummer der
          Kasse; Namen gibt die Anwendung nicht heraus. Der Einzelpreis
          am Sofortstorno kommt aus dem Archiv — er ist die einzige
          Zahl, die ihn beziffert.
        </p>
      </Karte>
    </>
  )
}
